import { installmentAmount } from "../../../components/paiement/financial";
// Calculs purs de la page de campagne v2 (aucun React, aucun appel réseau).
//
// Les règles de progression et de cycle de vie reprennent celles de l'API
// (api/services/campagne_model.js et campagne_public.js goalProgress) : la page les
// applique aux données déjà reçues au lieu d'un second appel toutes les 5 secondes.

// ---------------------------------------------------------------------------
// Couleurs : palette accessible dérivée d'une seule couleur de marque
// ---------------------------------------------------------------------------

export const AMBIANCES = {
    sanctuaire: "#1C5A8E",
    or: "#8A5F1E",
    eau: "#0B6E77",
    bordeaux: "#8E2C3B",
}

const INK = "#141719"
const WHITE = "#FFFFFF"

export function parseColor(value) {
    if (typeof value !== "string") return null
    const hex = value.trim().replace(/^#/, "")
    if (/^[0-9a-f]{3}$/i.test(hex)) {
        return hex.split("").map(c => parseInt(c + c, 16))
    }
    if (/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(hex)) {
        return [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16))
    }
    const rgb = value.match(/^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i)
    if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])].map(n => Math.max(0, Math.min(255, n)))
    return null
}

function toHex(rgb) {
    return "#" + rgb.map(n => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, "0")).join("").toUpperCase()
}

function luminance(rgb) {
    const channel = (v) => {
        const s = v / 255
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
    }
    return 0.2126 * channel(rgb[0]) + 0.7152 * channel(rgb[1]) + 0.0722 * channel(rgb[2])
}

export function contrastRatio(a, b) {
    const ca = typeof a === "string" ? parseColor(a) : a
    const cb = typeof b === "string" ? parseColor(b) : b
    if (!ca || !cb) return 1
    const la = luminance(ca)
    const lb = luminance(cb)
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

function mix(rgb, target, ratio) {
    return rgb.map((v, i) => v + (target[i] - v) * ratio)
}

// Assombrit la couleur jusqu'à atteindre le contraste demandé avec `against`.
function darkenUntil(rgb, against, minRatio) {
    let current = rgb
    for (let i = 0; i < 40 && contrastRatio(current, against) < minRatio; i++) {
        current = mix(current, [0, 0, 0], 0.06)
    }
    return current
}

/**
 * Palette de la page à partir des réglages de la campagne.
 * Garantit : texte blanc lisible sur la couleur principale (4,5:1), texte coloré
 * lisible sur fond blanc (4,5:1), teintes claires des montants où le texte sombre
 * reste lisible. La couleur saisie n'est jamais utilisée telle quelle si elle échoue.
 */
export function buildPalette(config = {}, count = 6) {
    const tokenPrimary = parseColor(config?.tokens?.main?.primary)
    const legacyPrimary = parseColor(config?.primaryColor)
    const ambiance = parseColor(AMBIANCES[config?.ambiance] || "")
    const base = tokenPrimary || legacyPrimary || ambiance || parseColor(AMBIANCES.sanctuaire)

    const white = parseColor(WHITE)
    const accent = darkenUntil(base, white, 4.5)
    const deep = darkenUntil(mix(accent, [0, 0, 0], 0.22), white, 7)
    const light = mix(base, white, 0.9)

    const tokenDark = parseColor(config?.tokens?.main?.dark)
    const dark = tokenDark && contrastRatio(tokenDark, white) >= 7 ? tokenDark : parseColor(INK)

    const n = Math.max(1, count)
    const tints = []
    const borders = []
    for (let i = 0; i < n; i++) {
        const step = n === 1 ? 0 : i / (n - 1)
        tints.push(toHex(mix(base, white, 0.955 - 0.2 * step)))
        borders.push(toHex(mix(base, white, 0.85 - 0.3 * step)))
    }

    return {
        accent: toHex(accent),
        accentDeep: toHex(deep),
        accentLight: toHex(light),
        onAccent: WHITE,
        dark: toHex(dark),
        tints,
        borders,
    }
}

// ---------------------------------------------------------------------------
// Cycle de vie et progression (mêmes règles que l'API)
// ---------------------------------------------------------------------------

const STATUSES = ["DRAFT", "LIVE", "CLOSED", "ARCHIVED", "PUBLISHED", "PAUSED"]

function toDate(value) {
    if (value === null || value === undefined || value === "") return null
    const date = value instanceof Date ? value : new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Mêmes règles que api/services/campagne_model.js : les dates sont indicatives,
 * sauf politique SCHEDULED explicite. Les fermetures manuelles sont conservées.
 */
export function lifecycle(campagne, now = new Date()) {
    const permanent = campagne?.config?.permanent === true
    const start = toDate(campagne?.dateStart)
    const end = permanent ? null : toDate(campagne?.dateEnd)
    const notStarted = !!(start && start.getTime() > now.getTime())
    const ended = !!(end && end.getTime() < now.getTime())
    const scheduledEnd = toDate(campagne?.dateEnd)
    const scheduledOpen = !notStarted && !(scheduledEnd && scheduledEnd.getTime() < now.getTime())
    const afterEnd = campagne?.donationsAfterEnd === true
    const closeAt = campagne?.donationPolicy === "SCHEDULED" ? scheduledEnd : null
    const late = ended && campagne?.donationPolicy !== "SCHEDULED"
    const base = {notStarted, ended, late, start, end, closeAt, permanent}

    if (!STATUSES.includes(campagne?.status)) {
        let status = "LIVE"
        if (campagne?.active === false || (campagne?.donationPolicy === "SCHEDULED" && !scheduledOpen)) status = "CLOSED"
        return {status, explicit: false, ...base, donationsOpen: campagne?.donationPolicy !== "SCHEDULED" || scheduledOpen}
    }
    if (campagne.status === "DRAFT" || campagne.status === "ARCHIVED") {
        return {status: campagne.status, explicit: true, ...base, donationsOpen: false}
    }
    if (campagne.status === "PAUSED") {
        return {status: "CLOSED", explicit: true, ...base, ended: true, end: null, donationsOpen: false}
    }
    if (campagne.status === "CLOSED") {
        // Campagne close à la main AVANT sa date de fin : le compte à rebours continuait
        // d'annoncer un temps restant juste au-dessus de « Cette campagne est terminée ».
        // Une date de fin encore à venir n'a plus rien à annoncer.
        const closedEarly = !ended
        return {
            status: "CLOSED",
            explicit: true,
            ...base,
            ended: true,
            end: closedEarly ? null : base.end,
            donationsOpen: afterEnd,
        }
    }
    if (campagne.donationPolicy === "SCHEDULED") {
        const open = scheduledOpen
        return {status: open ? "LIVE" : "CLOSED", explicit: true, ...base, donationsOpen: open}
    }
    return {status: "LIVE", explicit: true, ...base, donationsOpen: true}
}

/**
 * Où envoyer un visiteur quand la campagne est fermée : l'adresse choisie pour la campagne,
 * sinon le bouton « Faire un don » du site de l'organisation, sinon sa page de don.
 */
export function afterCloseLink(campagne, org) {
    const custom = typeof campagne?.config?.afterCloseUrl === "string" ? campagne.config.afterCloseUrl.trim() : ""
    if (custom) return custom
    // Sur une page de campagne, `BASE_WEB_URL` est l'adresse de la campagne elle-même :
    // le don général vit sur le site de l'organisation, donc `WEB_URL`.
    const base = String(org?.WEB_URL || org?.BASE_WEB_URL || "").replace(/\/+$/, "")
    // Le bouton principal du menu du site est celui du don (« Faire un don » → /don).
    const button = org?.web?.menu?.button_1_url
    const path = (typeof button === "string" && button.trim() ? button : "/don").trim()
    if (/^https?:\/\//i.test(path)) return path
    return base + (path.startsWith("/") ? path : "/" + path)
}

function toNumber(value) {
    if (value === null || value === undefined || value === "") return null
    const n = Number(typeof value === "string" ? value.replace(",", ".") : value)
    return Number.isFinite(n) ? n : null
}

export function goalOf(campagne) {
    const mode = campagne?.type === "PARTNER" ? "UNITS" : (campagne?.type === "COUNT" ? "COUNT" : "AMOUNT")
    const bonus = Array.isArray(campagne?.bonus)
        ? [...new Set(campagne.bonus.map(toNumber).filter(n => n !== null && n > 0))].sort((a, b) => a - b)
        : []
    const config = campagne?.config || {}
    const one = typeof config.donorName === "string" && config.donorName.trim() ? config.donorName.trim() : null
    const other = typeof config.donorNameMulti === "string" && config.donorNameMulti.trim() ? config.donorNameMulti.trim() : one
    return {
        mode,
        target: toNumber(campagne?.objectif) || 0,
        bonus,
        unitPrice: mode === "UNITS" ? (toNumber(campagne?.partnerMontant) || null) : null,
        unitLabel: mode === "UNITS" ? {one, other} : null,
    }
}

export function goalProgress(goal, totals) {
    const amount = totals?.sum?.total || 0
    const donations = totals?.count?.total || 0
    let value = amount
    if (goal.mode === "COUNT") value = donations
    if (goal.mode === "UNITS") value = amount / (goal.unitPrice || 1)

    let target = goal.target
    let activeBonus = null
    if (target > 0 && target <= value && goal.bonus.length > 0) {
        const suivant = goal.bonus.find(b => b > value)
        const cible = suivant ?? goal.bonus[goal.bonus.length - 1]
        if (cible > target) target = cible
        // Un palier DÉJÀ dépassé n'est plus un objectif : l'annoncer comme « nouveau
        // palier » alors qu'il est franchi trompe le donateur.
        activeBonus = suivant && suivant > goal.target ? suivant : null
    }
    const percentOf = (t) => (t > 0 ? Math.round((value / t) * 1000) / 10 : 0)
    return {
        value,
        amount,
        donations,
        target,
        initialTarget: goal.target,
        activeBonus,
        reached: goal.target > 0 && value >= goal.target,
        percent: percentOf(target),
        initialPercent: percentOf(goal.target),
    }
}

export function timeLeft(end, now = new Date()) {
    const date = toDate(end)
    if (!date) return null
    const ms = date.getTime() - now.getTime()
    if (ms <= 0) return {ms: 0, days: 0, hours: 0, minutes: 0, seconds: 0}
    const seconds = Math.floor(ms / 1000)
    return {
        ms,
        days: Math.floor(seconds / 86400),
        hours: Math.floor((seconds % 86400) / 3600),
        minutes: Math.floor((seconds % 3600) / 60),
        seconds: seconds % 60,
    }
}

// Limite de réservation du moteur commun (api/services/fundraising_model.js).
export const MAX_PRODUCT_QUANTITY = 10000

// ---------------------------------------------------------------------------
// Avantage fiscal affiché au donateur, selon le pays de l'organisme émetteur
// ---------------------------------------------------------------------------

export function taxBenefit(country, config = {}) {
    const code = String(country || "").toUpperCase()
    if (code === "FR") return {kind: "REDUCTION", rate: 0.66}
    if (code === "IL") return {kind: "CREDIT", rate: 0.35}
    if (code === "UK" || code === "GB") return {kind: "GIFT_AID", rate: 0.25, counted: config?.giftAidBoost === true}
    return null
}

// Coût réel pour un particulier (arrondi à l'euro supérieur, jamais sous zéro).
export function realCost(amount, benefit) {
    if (!benefit || !(amount > 0) || benefit.kind === "GIFT_AID") return null
    return Math.max(0, Math.ceil(amount * (1 - benefit.rate)))
}

// ---------------------------------------------------------------------------
// Montants proposés
// ---------------------------------------------------------------------------

export function sortedTiers(products = []) {
    return [...products]
        .filter(p => p && Number(p.montant) > 0)
        .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0))
}

/**
 * Don repris depuis un lien (`?don=44`, envoyé par l'email de rattrapage) : le palier
 * du même montant s'il est encore disponible, sinon ce montant en don libre s'il est
 * permis, sinon rien — le donateur choisit alors comme d'habitude.
 */
export function resumeChoice(tiers = [], raw, {freeAmountAllowed = true, priceOf = (p) => Number(p.montant)} = {}) {
    if (raw === null || raw === undefined || Array.isArray(raw)) return null
    const amount = Number(String(raw).replace(",", "."))
    if (!Number.isFinite(amount) || amount <= 0) return null
    const tier = tiers.find(p => Math.abs(Number(priceOf(p)) - amount) < 0.005)
    if (tier) return {tierId: tier.id, freeAmount: ""}
    if (freeAmountAllowed) return {tierId: null, freeAmount: String(amount)}
    return null
}

export function resumeCurrency(raw, options = []) {
    if (typeof raw !== "string" || !/^[a-z]{3}$/i.test(raw)) return null
    return options.find(option => option.devise === raw.toLowerCase()) || null
}

// Un lien de tombola identifie le lot et le nombre de billets : le total seul peut
// correspondre à un autre lot. Un stock ou un prix modifié annule la présélection.
export function resumeProduct(tiers, rawId, rawQuantity, stockOf, rawAmount, priceOf) {
    if (typeof rawId !== "string" || Array.isArray(rawQuantity) || !/^[1-9]\d*$/.test(String(rawQuantity))) return null
    const quantity = Number(rawQuantity)
    if (!Number.isSafeInteger(quantity) || quantity > MAX_PRODUCT_QUANTITY) return null
    const tier = tiers.find(p => p.id === rawId)
    const stock = tier ? stockOf(tier) : 0
    const amount = Number(rawAmount)
    if (!tier || (stock !== null && stock >= 0 && stock < quantity) || !Number.isFinite(amount) || amount <= 0) return null
    if (Math.abs(Number(priceOf(tier)) * quantity - amount) >= 0.005) return null
    return {tierId: tier.id, quantity}
}

export function targetWithTeam(target, team) {
    const result = {...target}
    if (team) result.team = team
    else delete result.team
    return result
}

export function stockOf(campagne, productId) {
    const stocks = campagne?.stats?.total?.stocks
    if (!Array.isArray(stocks)) return null
    const entry = stocks.find(s => s.id === productId)
    return entry && typeof entry.stock === "number" ? entry.stock : null
}

export function initials(name) {
    const words = String(name || "").trim().split(/\s+/).filter(Boolean)
    if (words.length === 0) return "?"
    const letters = words.length === 1 ? words[0].slice(0, 1) : words[0].slice(0, 1) + words[words.length - 1].slice(0, 1)
    return letters.toUpperCase()
}

// Codes pays historiques des associations qui ne sont pas des codes ISO.
const REGION_ALIASES = {UK: "GB"}

/** Nom du pays dans la langue de la page (« France », « צרפת »…), ou le code à défaut. */
export function countryName(code, langValue = "fr") {
    if (!code) return null
    const region = REGION_ALIASES[String(code).toUpperCase()] || String(code).toUpperCase()
    try {
        return new Intl.DisplayNames([langValue || "fr"], {type: "region"}).of(region) || code
    } catch (e) {
        return code
    }
}

// Mensualités proposées d'office : celles où l'arrondi au supérieur de la mensualité
// (ce que prélève la plateforme) ajoute le moins au don, la plus longue à égalité.
export function defaultSplit(amount, options = []) {
    let best = options[options.length - 1] || 0
    let bestExtra = Infinity
    for (const n of [...options].sort((a, b) => b - a)) {
        const extra = installmentAmount(amount, n) * n - amount
        if (extra < bestExtra) {
            best = n
            bestExtra = extra
        }
    }
    return best
}

/**
 * Façon de donner d'une campagne, lue dans les réglages que l'ancienne page lit aussi :
 *   MONTHLY : don mensuel (productMonth) ;
 *   CHOICE  : au choix du donateur, une fois ou chaque mois (frequencyChoice) ;
 *   ONCE    : don unique (maxDivideNB = 1) ;
 *   SPLIT   : don unique, étalable jusqu'à maxDivideNB mois (12 par défaut).
 * Formulation d'un don étalé : en total (« 1 800 € en 12 fois ») ou en mensualité
 * (monthlyAmountDisplay). Durée d'un don mensuel : au choix du donateur, imposée
 * (forceMonthNb > 0) ou sans fin (forceMonthNb = 0).
 */
export function givingModel(config = {}, {stockable = false} = {}) {
    const forced = config.forceMonthNb !== undefined && config.forceMonthNb !== null && config.forceMonthNb !== ""
    const maxDivide = Number(config.maxDivideNB)
    let mode = "SPLIT"
    if (config.productMonth === true) mode = "MONTHLY"
    else if (!stockable && config.frequencyChoice === true) mode = "CHOICE"
    else if (maxDivide === 1) mode = "ONCE"
    return {
        mode,
        ...(stockable ? {stockable:true} : {}),
        maxSplit: mode === "SPLIT" ? (maxDivide > 1 ? maxDivide : 12) : 0,
        wording: config.monthlyAmountDisplay === true ? "MONTHLY" : "TOTAL",
        duration: !forced ? "CHOICE" : (Number(config.forceMonthNb) > 0 ? "FIXED" : "OPEN"),
        fixedMonths: forced ? Number(config.forceMonthNb) || 0 : null,
    }
}

/**
 * Le don est-il mensuel ? Un montant proposé peut avoir son propre rythme
 * (`frequency` : ONCE | MONTHLY) ; en « au choix », c'est la bascule du donateur.
 */
export function isMonthlyDonation(giving, tier = null, choice = "ONCE") {
    if (giving.stockable) return giving.mode === "MONTHLY"
    // Le rythme écrit sur le montant l'emporte toujours : un palier « chaque mois » ne
    // doit jamais être facturé en une fois parce que le donateur a laissé la bascule
    // sur « Une fois », ni l'inverse.
    if (tier?.frequency === "MONTHLY") return true
    if (tier?.frequency === "ONCE") return false
    if (giving.mode === "CHOICE") return choice === "MONTHLY"
    return giving.mode === "MONTHLY"
}

/**
 * Montants affichés. En « au choix », la série du rythme choisi : les montants mensuels
 * sous « Chaque mois », les autres sous « Une fois ». Les montants sans rythme propre
 * servent aux deux ; une série qui n'en contient aucun reste vide (le donateur passe par
 * le montant libre) plutôt que d'afficher des montants de l'autre rythme.
 */
export function visibleTiers(giving, tiers = [], choice = "ONCE") {
    if (giving.mode !== "CHOICE") return tiers
    const monthly = choice === "MONTHLY"
    const serie = tiers.filter(p => (p?.frequency === "MONTHLY") === monthly)
    if (serie.length > 0) return serie
    return tiers.filter(p => p?.frequency !== "MONTHLY" && p?.frequency !== "ONCE")
}
